// Service API pour remplacer Dexie par des appels serveur
// Détection automatique de l'environnement
const getApiUrl = () => {
    // Détection automatique basée sur l'hostname et le protocole
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    console.log('🔍 Détection environnement - hostname:', hostname, 'protocol:', protocol);

    // Cas 1: localhost / 127.0.0.1 → API locale
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        console.log('🏠 Environnement LOCAL (localhost) détecté');
        return 'http://localhost:3002';
    }

    // Cas 2: IP privée LAN → utiliser la même IP pour joindre l'API (pas localhost)
    const isPrivateLanIp = /^10\./.test(hostname) || /^192\.168\./.test(hostname) || /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname);
    if (isPrivateLanIp) {
        console.log('🏠 Environnement LOCAL (LAN) détecté (ignore REACT_APP_API_URL si présent)');
        return `${protocol}//${hostname}:3002`;
    }

    // Cas 3: production (domaine) - préférer domaine API si REACT_APP_API_URL pointe sur localhost
    const envUrl = process.env.REACT_APP_API_URL;
    if (envUrl) {
        console.log('🔧 Utilisation de REACT_APP_API_URL:', envUrl);
        if (/localhost|127\.0\.0\.1/.test(envUrl)) {
            console.log('🌐 REACT_APP_API_URL pointe vers localhost en prod, fallback sur domaine API');
            return 'https://api.astreintes.provserver.ovh';
        }
        return envUrl;
    }
    console.log('🌐 Environnement PRODUCTION détecté');
    return 'https://api.astreintes.provserver.ovh';
};

const API_BASE_URL = getApiUrl();
const API_TIMEOUT = 300000; // 300 secondes (5 minutes) pour les planifications complexes

console.log('🔧 Service API initialisé avec l\'URL:', API_BASE_URL);
console.log('🌐 Variables d\'environnement REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
console.log('⏰ Configuration API rechargée le:', new Date().toLocaleTimeString());
console.log('🆕 Version API: 2.0 - getManagedTeams corrigé');

class ApiError extends Error {
    constructor(message, status, response) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.response = response;
    }
}

// Fonction utilitaire pour les requêtes HTTP
async function apiRequest(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Récupérer l'utilisateur connecté et l'équipe sélectionnée pour l'isolation
    const currentUser = localStorage.getItem('currentUser');
    const selectedTeamId = localStorage.getItem('selectedTeamId');
    let userId = null;
    if (currentUser) {
        try {
            const user = JSON.parse(currentUser);
            userId = user.id;
            // console.log('🔍 API Service - userId récupéré:', userId, 'pour', user.email);
        } catch (e) {
            console.warn('⚠️ Erreur parsing currentUser:', e);
        }
    } else {
        // console.warn('⚠️ API Service - currentUser non trouvé dans localStorage');
    }
    
    // Récupérer un éventuel token JWT depuis le stockage local
    const storedToken =
        localStorage.getItem('authToken') ||
        localStorage.getItem('token') ||
        localStorage.getItem('jwt') ||
        localStorage.getItem('jwtToken');

    const config = {
        headers: {
            'Content-Type': 'application/json',
            ...(userId && { 'x-user-id': userId.toString() }), // Ajouter l'ID utilisateur pour l'isolation
            ...(selectedTeamId && { 'x-team-id': selectedTeamId }), // Ajouter l'équipe sélectionnée
            ...(storedToken && { Authorization: `Bearer ${storedToken}` }),
            ...options.headers,
        },
        ...options,
    };

    try {
        console.log(`🌐 API Request: ${config.method || 'GET'} ${url}`);
        console.log('📡 Avant fetch()...');
        
        // Ajouter un timeout plus long
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
            console.log(`⏰ TIMEOUT - Annulation de la requête après ${API_TIMEOUT/1000}s`);
            controller.abort();
        }, API_TIMEOUT);
        
        const response = await fetch(url, {
            ...config,
            signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        console.log('📥 Après fetch(), avant response.ok check:', response.status, response.statusText);
        
        if (!response.ok) {
            console.log('❌ Response not ok, processing error...');
            const errorText = await response.text();
            let errorData;
            try {
                errorData = JSON.parse(errorText);
            } catch {
                errorData = { error: errorText };
            }
            // Gestion spéciale des erreurs de permissions (403)
            if (response.status === 403 && errorData.warning) {
                console.warn('⚠️ Permission refusée:', errorData);
                
                // Afficher une notification d'avertissement
                if (window.showPermissionWarning) {
                    window.showPermissionWarning(errorData);
                } else {
                    // Fallback : afficher dans la console avec plus de détails
                    console.warn(`🚫 Action non autorisée: ${errorData.requiredPermission?.action} sur ${errorData.requiredPermission?.module}`);
                    console.warn(`👤 Rôle: ${errorData.userRole}, Équipe: ${errorData.teamName}`);
                    console.warn(`💡 Conseil: Demandez les permissions de délégation ou contactez un administrateur`);
                }
            }
            
            throw new ApiError(
                errorData.error || `HTTP ${response.status}`,
                response.status,
                errorData
            );
        }

        console.log('✅ Response ok, parsing JSON...');
        const data = await response.json();
        console.log(`✅ API Response: ${config.method || 'GET'} ${url}`, data);
        return data;
    } catch (error) {
        if (error.name === 'AbortError') {
            console.error(`⏰ TIMEOUT: ${config.method || 'GET'} ${url} - Requête annulée après ${API_TIMEOUT/1000}s`);
            throw new ApiError(`Timeout de la requête (${API_TIMEOUT/1000}s)`, 408, { originalError: error });
        }
        
        if (error instanceof ApiError) {
            console.error(`❌ API Error: ${config.method || 'GET'} ${url}`, error);
            throw error;
        }
        
        console.error(`❌ Network Error: ${config.method || 'GET'} ${url}`, error);
        throw new ApiError('Erreur de connexion au serveur', 0, { originalError: error });
    }
}

// === SERVICE TECHNICIENS ===
export const technicianService = {
    // Récupérer tous les techniciens
    getAll: (teamId = null) => {
        const params = teamId ? `?teamId=${teamId}` : '';
        return apiRequest(`/api/technicians${params}`);
    },

    // Récupérer un technicien par ID
    getById: (id) => apiRequest(`/api/technicians/${id}`),

    // Créer un nouveau technicien
    create: (technicianData) => apiRequest('/api/technicians', {
        method: 'POST',
        body: JSON.stringify(technicianData),
    }),

    // Mettre à jour un technicien
    update: (id, technicianData) => apiRequest(`/api/technicians/${id}`, {
        method: 'PUT',
        body: JSON.stringify(technicianData),
    }),

    // Supprimer un technicien (soft delete)
    delete: (id) => apiRequest(`/api/technicians/${id}`, {
        method: 'DELETE',
    }),
};

// === SERVICE COMPÉTENCES ===
export const skillService = {
    // Récupérer toutes les compétences
    getAll: (teamId = null) => {
        const params = teamId ? `?teamId=${teamId}` : '';
        return apiRequest(`/api/skills${params}`);
    },

    // Récupérer une compétence par ID
    getById: (id) => apiRequest(`/api/skills/${id}`),

    // Créer une nouvelle compétence
    create: (skillData) => apiRequest('/api/skills', {
        method: 'POST',
        body: JSON.stringify(skillData),
    }),

    // Mettre à jour une compétence
    update: (id, skillData) => apiRequest(`/api/skills/${id}`, {
        method: 'PUT',
        body: JSON.stringify(skillData),
    }),

    // Supprimer une compétence
    delete: (id) => apiRequest(`/api/skills/${id}`, {
        method: 'DELETE',
    }),
};

// === SERVICE ASTREINTES ===
export const onCallService = {
    // Récupérer toutes les astreintes
    getAll: (teamId = null) => {
        const params = teamId ? `?teamId=${teamId}` : '';
        return apiRequest(`/api/oncalls${params}`);
    },

    // Récupérer une astreinte par ID
    getById: (id) => apiRequest(`/api/oncalls/${id}`),

    // Créer une nouvelle astreinte
    create: (onCallData) => apiRequest('/api/oncalls', {
        method: 'POST',
        body: JSON.stringify(onCallData),
    }),

    // Mettre à jour une astreinte
    update: (id, onCallData) => apiRequest(`/api/oncalls/${id}`, {
        method: 'PUT',
        body: JSON.stringify(onCallData),
    }),

    // Supprimer une astreinte
    delete: (id) => apiRequest(`/api/oncalls/${id}`, {
        method: 'DELETE',
    }),

    // Synchronisation intelligente depuis import Liisa
    bulkSync: ({ toCreate = [], toDelete = [], rangeStart, rangeEnd } = {}) =>
        apiRequest('/api/oncalls/bulk', {
            method: 'POST',
            body: JSON.stringify({ toCreate, toDelete, rangeStart, rangeEnd }),
        }),
};

// === SERVICE ABSENCES ===
export const absenceService = {
    // Récupérer toutes les absences (avec option de limitation)
    getAll: (teamId = null, options = {}) => {
        const params = new URLSearchParams();
        if (teamId) params.set('teamId', teamId);
        if (options.limitToRecentMonths) params.set('limitToRecentMonths', options.limitToRecentMonths);
        if (options.fullLoad) params.set('fullLoad', 'true');
        
        const paramString = params.toString();
        return apiRequest(`/api/absences${paramString ? `?${paramString}` : ''}`);
    },

    // Récupérer une absence par ID
    getById: (id) => apiRequest(`/api/absences/${id}`),

    // Créer une nouvelle absence
    create: (absenceData) => apiRequest('/api/absences', {
        method: 'POST',
        body: JSON.stringify(absenceData),
    }),

    // Mettre à jour une absence
    update: (id, absenceData) => apiRequest(`/api/absences/${id}`, {
        method: 'PUT',
        body: JSON.stringify(absenceData),
    }),

    // Supprimer une absence
    delete: (id) => apiRequest(`/api/absences/${id}`, {
        method: 'DELETE',
    }),

    // Synchronisation en masse (create/update/delete en une seule requête)
    bulkSync: ({ toCreate = [], toUpdate = [], toDelete = [] } = {}) =>
        apiRequest('/api/absences/bulk', {
            method: 'POST',
            body: JSON.stringify({ toCreate, toUpdate, toDelete }),
        }),
};

// === SERVICE TEST ===
export const testService = {
    // Tester la connexion à la base de données
    async testDatabase() {
        return apiRequest('/api/test-db');
    },
};

// Export de l'erreur API pour la gestion d'erreurs
export { ApiError };

// Configuration par défaut
export const API_CONFIG = {
    baseUrl: API_BASE_URL,
    timeout: API_TIMEOUT,
};

// === SERVICE AUTHENTIFICATION ===
export const authService = {
    // Connexion utilisateur
    login: (email, password) => apiRequest('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
    }),

    // Vérifier si un utilisateur existe (pour restauration de session)
    verify: (userId) => apiRequest('/api/auth/verify', {
        method: 'POST',
        body: JSON.stringify({ userId }),
    }),

    // Déconnexion (côté client seulement)
    logout: () => {
        // Nettoyer les données locales
        localStorage.removeItem('currentUser');
        localStorage.removeItem('selectedTeamId');
        return Promise.resolve();
    }
};

// === SERVICE UTILISATEURS ===
export const userService = {
    // Récupérer tous les utilisateurs
    getAll: () => apiRequest('/api/users'),
    
    // Récupérer un utilisateur par ID
    getById: (id) => apiRequest(`/api/users/${id}`),
    
    // Créer un nouvel utilisateur
    create: (userData) => apiRequest('/api/users', {
        method: 'POST',
        body: JSON.stringify(userData),
    }),
    
    // Mettre à jour un utilisateur
    update: (id, userData) => apiRequest(`/api/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(userData),
    }),
    
    // Supprimer un utilisateur
    delete: (id) => apiRequest(`/api/users/${id}`, {
        method: 'DELETE',
    }),
};

// === SERVICE ÉQUIPE ===
export const teamService = {
    // Récupérer toutes les équipes (accessible aux SuperAdmin uniquement)
    getAll: () => apiRequest('/api/teams'),
    
    // Récupérer une équipe spécifique
    getById: (id) => apiRequest(`/api/teams/${id}`),
    
    // Créer une nouvelle équipe
    create: (teamData) => apiRequest('/api/teams', {
        method: 'POST',
        body: JSON.stringify(teamData),
    }),
    
    // Mettre à jour une équipe
    update: (id, teamData) => apiRequest(`/api/teams/${id}`, {
        method: 'PUT',
        body: JSON.stringify(teamData),
    }),
    
    // Supprimer une équipe (soft delete)
    delete: (id) => apiRequest(`/api/teams/${id}`, {
        method: 'DELETE',
    }),
    
    // Récupérer les données d'une équipe spécifique
    getTechnicians: (teamId) => apiRequest(`/api/teams/${teamId}/technicians`),
    getSkills: (teamId) => apiRequest(`/api/teams/${teamId}/skills`),
    getOnCalls: (teamId) => apiRequest(`/api/teams/${teamId}/oncalls`),
    getAbsences: (teamId) => apiRequest(`/api/teams/${teamId}/absences`),
};

// === SERVICE EMAIL TEMPLATE ===
export const emailTemplateService = {
    // Récupérer le template HTML d'email
    getTemplate: () => apiRequest('/api/email-template'),
    
    // Générer un aperçu du template HTML avec des données de test
    renderPreview: (html, data) => apiRequest('/api/render-email-template', {
        method: 'POST',
        body: JSON.stringify({ html, ...data }),
    }),
};

// Fonction d'envoi d'email
export const sendEmail = (emailData) => apiRequest('/api/send-email', {
    method: 'POST',
    body: JSON.stringify(emailData),
});

// Fonction de rendu du template email (compatible avec l'ancienne interface)
export const renderEmailTemplate = (html, data) => {
    return emailTemplateService.renderPreview(html, data);
};

// === SERVICE ÉQUIPES ===
export const teamsService = {
    // CRUD des équipes
    getAll: () => apiRequest('/api/teams'),
    getById: (id) => apiRequest(`/api/teams/${id}`),
    create: (teamData) => apiRequest('/api/teams', {
        method: 'POST',
        body: JSON.stringify(teamData)
    }),
    update: (id, teamData) => apiRequest(`/api/teams/${id}`, {
        method: 'PUT',
        body: JSON.stringify(teamData)
    }),
    delete: (id) => apiRequest(`/api/teams/${id}`, {
        method: 'DELETE'
    }),
    
    // Délégations
    addDelegation: (teamId, delegationData) => apiRequest(`/api/teams/${teamId}/delegations`, {
        method: 'POST',
        body: JSON.stringify(delegationData)
    }),
    removeDelegation: (teamId, userId) => apiRequest(`/api/teams/${teamId}/delegations/${userId}`, {
        method: 'DELETE'
    }),
    
    // Équipes gérées par un utilisateur (avec cache simple)
    getManagedTeams: (() => {
        const cache = new Map();
        const CACHE_TTL = 30000; // 30 secondes
        
        return (userId) => {
            const cacheKey = `managed-teams-${userId}`;
            const now = Date.now();
            
            // Vérifier le cache
            const cached = cache.get(cacheKey);
            if (cached && (now - cached.timestamp) < CACHE_TTL) {
                console.log('📦 Cache client hit pour managed-teams:', userId);
                return Promise.resolve(cached.data);
            }
            
            console.log('🔄 Cache client miss, appel API managed-teams:', userId);
            const promise = apiRequest(`/api/users/${userId}/managed-teams`);
            
            // Mettre en cache après la réponse
            promise.then(data => {
                cache.set(cacheKey, {
                    data,
                    timestamp: now
                });
            });
            
            return promise;
        };
    })(),
    
    // Paramètres d'équipe
    updateSettings: (teamId, settings) => apiRequest(`/api/teams/${teamId}/settings`, {
        method: 'PUT',
        body: JSON.stringify(settings)
    }),
    getSettings: (teamId) => apiRequest(`/api/teams/${teamId}/settings`),
};

// Export par défaut pour la compatibilité
export default {
    get: (endpoint) => apiRequest(endpoint),
    post: (endpoint, data) => apiRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    put: (endpoint, data) => apiRequest(endpoint, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    delete: (endpoint) => apiRequest(endpoint, {
        method: 'DELETE'
    })
};